import { ANALYTICS_CONSENT_VERSION, analyticsUuid, parseAnalyticsInput, type AnalyticsEventName, type AnalyticsProperties } from "./analytics-contract";
import type { AnalyticsIdentifiers } from "./tags-contract";
export type AnalyticsConsent = "unknown" | "granted" | "denied";
export function createAnalyticsTracker(dependencies: {
  consent: () => AnalyticsConsent; anonymous: { read: () => string | null; write: (id: string) => void; clear: () => void };
  transport: typeof fetch; uuid: () => string; onFailure?: (stage: "identity" | "payload" | "transport" | "response") => void;
  /** Browser identifiers from the workspace tags (GA client id…), when they are mounted. */
  identifiers?: () => AnalyticsIdentifiers | undefined;
}) {
  const pending = new Set<AbortController>();
  const report = (stage: "identity" | "payload" | "transport" | "response") => { try { dependencies.onFailure?.(stage); } catch { /* Diagnostics are not user-facing work. */ } };
  const reset = () => { for (const controller of pending) controller.abort(); pending.clear(); dependencies.anonymous.clear(); };
  return { reset, async track(event: AnalyticsEventName, properties: AnalyticsProperties): Promise<boolean> {
    let stage: "identity" | "payload" | "transport" | "response" = "identity";
    try {
      if (dependencies.consent() !== "granted") { reset(); return false; }
      if (pending.size >= 4) return false;
      let anonymousId = dependencies.anonymous.read();
      if (!analyticsUuid(anonymousId)) { anonymousId = dependencies.uuid(); dependencies.anonymous.write(anonymousId); }
      stage = "payload";
      const identifiers = dependencies.identifiers?.();
      const input = parseAnalyticsInput({ event, properties, anonymousId, messageId: dependencies.uuid(), consentVersion: ANALYTICS_CONSENT_VERSION, ...(identifiers ? { identifiers } : {}) });
      const controller = new AbortController(); pending.add(controller);
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        stage = "transport";
        const response = await dependencies.transport("/api/events", { method: "POST", credentials: "same-origin", cache: "no-store", redirect: "error",
          headers: { "content-type": "application/json" }, body: JSON.stringify(input), signal: controller.signal });
        stage = "response";
        if (dependencies.consent() !== "granted" || controller.signal.aborted) return false;
        if (!response.ok) { report(stage); return false; }
        const result = await response.json(); return result.ok === true && result.state === "accepted";
      } finally { clearTimeout(timeout); pending.delete(controller); }
    } catch { report(stage); return false; } // No payloads, identifiers or raw errors in diagnostics.
  } };
}
